import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy, OnChanges, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { TransactionPersonnelleEnum } from 'projects/box-lib/src/lib/models/generated/graphql';
import { Subscription } from 'rxjs';


// Un statut enregistr? correspond ? z?ro, une ou deux cases coch?es.
const CHOICES_BY_STATUS: Record<TransactionPersonnelleEnum, readonly TransactionPersonnelleEnum[]> = {
  [TransactionPersonnelleEnum.Non]: [],
  [TransactionPersonnelleEnum.AutoSouscription]: [TransactionPersonnelleEnum.AutoSouscription],
  [TransactionPersonnelleEnum.PourProches]: [TransactionPersonnelleEnum.PourProches],
  [TransactionPersonnelleEnum.PourSalaries]: [TransactionPersonnelleEnum.PourSalaries],
  [TransactionPersonnelleEnum.AutoSouscriptionAvecProches]: [
    TransactionPersonnelleEnum.AutoSouscription,
    TransactionPersonnelleEnum.PourProches,
  ],
  [TransactionPersonnelleEnum.AutoSouscriptionAvecSalaries]: [
    TransactionPersonnelleEnum.AutoSouscription,
    TransactionPersonnelleEnum.PourSalaries,
  ],
};
const NO_CHOICES: readonly TransactionPersonnelleEnum[] = [];

@Component({
    selector: 'app-transaction-personnelle',
    templateUrl: './transaction-personnelle.component.html',
    styleUrls: ['./transaction-personnelle.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TransactionPersonnelleComponent implements OnChanges, OnDestroy {
  @Input() isPsi = false;
  @Input() withCoInvestisseur = false;

  readonly choices = [
    { value: TransactionPersonnelleEnum.AutoSouscription, label: 'Auto-souscription', control: new FormControl(false, { nonNullable: true }) },
    {
      value: TransactionPersonnelleEnum.PourProches,
      label: 'Personne(s) avec un lien familial ou étroit',
      control: new FormControl(false, { nonNullable: true }),
    },
    { value: TransactionPersonnelleEnum.PourSalaries, label: 'Un de mes salariés', control: new FormControl(false, { nonNullable: true }) },
  ];

  private get selectedChoices(): readonly TransactionPersonnelleEnum[] {
    const status = this.radioControl.value;
    return status == null ? NO_CHOICES : CHOICES_BY_STATUS[status];
  }

  isChecked(choice: TransactionPersonnelleEnum): boolean {
    return this.selectedChoices.includes(choice);
  }

  isChoiceDisabled(choice: TransactionPersonnelleEnum): boolean {
    const selected = this.selectedChoices;
    // Une case coch?e reste toujours d?cochable.
    if (selected.includes(choice)) return false;

    const maxChoices = this.withCoInvestisseur ? 2 : 1;
    if (selected.length >= maxChoices) return true;

    // Proches et salari?s sont incompatibles, m?me avec un co-investisseur.
    if (choice === TransactionPersonnelleEnum.PourProches) {
      return selected.includes(TransactionPersonnelleEnum.PourSalaries);
    }
    if (choice === TransactionPersonnelleEnum.PourSalaries) {
      return selected.includes(TransactionPersonnelleEnum.PourProches);
    }
    return false;
  }

  toggleChoice(choice: TransactionPersonnelleEnum, checked: boolean): void {
    if (checked && this.isChoiceDisabled(choice)) return;

    const selected = this.choices
      .filter(option => option.value === choice ? checked : this.isChecked(option.value))
      .map(option => option.value);

    this.radioControl.setValue(this.getStatusForChoices(selected));
  }

  private getStatusForChoices(selected: readonly TransactionPersonnelleEnum[]): TransactionPersonnelleEnum {
    if (selected.length < 2) return selected[0] ?? TransactionPersonnelleEnum.Non;
    if (selected.includes(TransactionPersonnelleEnum.PourProches)) {
      return TransactionPersonnelleEnum.AutoSouscriptionAvecProches;
    }
    return TransactionPersonnelleEnum.AutoSouscriptionAvecSalaries;
  }

  ngOnChanges(): void {
    // Sans co-investisseur, une combinaison redevient une auto-souscription seule.
    const mustResetCombination = this.isPsi && !this.withCoInvestisseur && this.selectedChoices.length > 1;
    if (mustResetCombination) {
      this.radioControl.setValue(TransactionPersonnelleEnum.AutoSouscription);
    }
    this.updateChoiceControls();
  }

  private updateChoiceControls(): void {
    for (const choice of this.choices) {
      const checked = this.isChecked(choice.value);
      const disabled = this.isChoiceDisabled(choice.value);

      if (choice.control.value !== checked) {
        choice.control.setValue(checked, { emitEvent: false });
      }
      if (choice.control.disabled === disabled) continue;

      if (disabled) {
        choice.control.disable({ emitEvent: false });
      } else {
        choice.control.enable({ emitEvent: false });
      }
    }
  }

  ngOnDestroy(): void {
    this.toggleSub?.unsubscribe();
    this.radioSub?.unsubscribe();
  }
  // those are defined by business and the id are from AtlasDB
  private readonly TRANSACTION_PERSONELLE_FULL_HABILITATION_IDS = [46, 47]; //CIF; ALPSI;
  private readonly TRANSACTION_PERSONNELLE_PARTIAL_HABILITATION_IDS = [44]; //ASSURANCE
  private _initValue: TransactionPersonnelleEnum | undefined;
  @Input({
    required: true,
  })
  set initValue(value: TransactionPersonnelleEnum | undefined) {
    if (this.isinitValueSet || this._initValue === value) {
      //do not launch init if already runned or if init value has not changed
      return;
    }
    this._initValue = value;
    this.isinitValueSet = true;
    this.initTransactionPersonnelle(value);
  }

  @Input({
    required: true,
  })
  set habilitationIds(value: Set<number>) {
    this.displaymode = 'no';
    this.isDisplaySet = false;
    if (this.TRANSACTION_PERSONNELLE_PARTIAL_HABILITATION_IDS.some(id => value?.has(id))) {
      this.displaymode = 'partial';
      this.isDisplaySet = true;
    }
    if (this.TRANSACTION_PERSONELLE_FULL_HABILITATION_IDS.some(id => value?.has(id))) {
      this.displaymode = 'full';
      this.isDisplaySet = true;
    }

    this.initTransactionPersonnelle(this._initValue);
  }

  @Output() onTransactionPersonnelleChange = new EventEmitter<TransactionPersonnelleEnum>();

  displaymode: 'no' | 'full' | 'partial' = 'no';
  toggleControl = new FormControl<boolean>(false);
  toggleSub: Subscription | undefined;
  radioSub: Subscription | undefined;
  radioControl = new FormControl<TransactionPersonnelleEnum>(TransactionPersonnelleEnum.Non);
  transactionOption = TransactionPersonnelleEnum;
  transactionValue: TransactionPersonnelleEnum;

  isDisplaySet = false;
  isinitValueSet = false;
  initTransactionPersonnelle(initValue: TransactionPersonnelleEnum | undefined) {
    if (!this.isDisplaySet) return;
    if (this.toggleSub) {
      this.toggleSub.unsubscribe();
    }
    if (this.radioSub) {
      this.radioSub.unsubscribe();
    }

    if (this.displaymode === 'full') {
      this.radioSub = this.radioControl.valueChanges.subscribe(value => {
        this.updateChoiceControls();
        this.onTransactionPersonnelleChange.emit(value ?? undefined);
      });
      this.radioControl.setValue(initValue ?? this.transactionOption.Non);
      if (!initValue) {
        this.onTransactionPersonnelleChange.emit(this.transactionOption.Non);
      }
    }

    if (this.displaymode === 'partial') {
      if (initValue) {
        this.toggleControl.setValue(initValue === this.transactionOption.AutoSouscription);
        this.toggleSub = this.toggleControl.valueChanges.subscribe(value => {
          if (value) {
            this.onTransactionPersonnelleChange.emit(this.transactionOption.AutoSouscription);
          } else {
            this.onTransactionPersonnelleChange.emit(this.transactionOption.Non);
          }
        });
      } else {
        this.onTransactionPersonnelleChange.emit(this.transactionOption.Non);
      }
    }
    if (this.displaymode === 'no') {
      this.onTransactionPersonnelleChange.emit(undefined);
    }
  }
}
